import { z } from "zod";

import { enumReadSchema } from "../../../core/enum-read.schema";

/**
 * Multi-currency money for Vibe billing.
 *
 * USD is hardcoded everywhere else in the codebase (token billing stores plain
 * `Int` ten-thousandths-of-USD columns). Vibe invoicing is the first multi-
 * currency surface, so it gets a real `Money` value object — a plain readonly
 * object plus pure functions, NOT a class. Reasons:
 *
 *   - It crosses the API boundary (invoice line items serialize to JSON); a
 *     class would not round-trip cleanly.
 *   - The `@nexus/types` package is runtime-light and functional by convention
 *     (see `model-catalog.config.ts` price helpers) — plain data + free
 *     functions tree-shake and stay serialization-safe.
 *
 * Precision mirrors the established `*InUSDTenThousandths` pattern: amounts are
 * integers in **ten-thousandths of one currency unit** (10_000 = one whole
 * unit, e.g. $1.00 or €1.00). One ten-thousandth resolves to $0.0001 — finer
 * than any real per-unit Vibe rate, so rounding loss is sub-cent per line.
 */

/** Ten-thousandths of one currency unit per whole unit (10_000 = 1.00). */
export const VIBE_MONEY_PRECISION = 10_000;

/**
 * Round UP to the next whole ten-thousandth, after stripping binary
 * floating-point noise. A product like `6 × 1.3` is `7.800000000000001` in
 * IEEE-754, and a naive `Math.ceil(780.0000000001)` would spuriously bill an
 * extra ten-thousandth. Rounding to 6 decimals first (far finer than one
 * ten-thousandth, far coarser than the ~1e-10 noise) removes the artefact while
 * preserving any genuine sub-unit remainder — which still ceils up, so we never
 * under-charge. This is THE rounding primitive for the rating engine; using it
 * everywhere keeps amounts deterministic and reproducible.
 */
export function ceilTenThousandths(value: number): number {
  return Math.ceil(Math.round(value * 1e6) / 1e6);
}

/**
 * Currencies Vibe can invoice in. USD is the base (AWS bills us in USD, so all
 * raw cost is captured in USD); the others are FX-converted targets. Kept as a
 * closed set so a stray currency code can never reach an invoice — adding one is
 * a deliberate edit here plus a price-policy review.
 */
export const VIBE_CURRENCIES = ["USD", "EUR", "GBP"] as const;

export type VibeCurrency = (typeof VIBE_CURRENCIES)[number];

export const VibeCurrencySchema = z.enum(VIBE_CURRENCIES);

/** `VibeCurrencySchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeCurrencyReadSchema = enumReadSchema(VIBE_CURRENCIES);

/** The base currency all raw Vibe cost is captured in (AWS bills USD). */
export const VIBE_BASE_CURRENCY: VibeCurrency = "USD";

/**
 * An immutable monetary amount. `amountTenThousandths` is always an integer;
 * construct via {@link money} so that invariant holds.
 */
export interface Money {
  readonly currency: VibeCurrency;
  readonly amountTenThousandths: number;
}

export const MoneySchema = z.object({
  currency: VibeCurrencySchema,
  amountTenThousandths: z.number().int()
});

/** Thrown when two amounts in different currencies are combined. */
export class CurrencyMismatchError extends Error {
  constructor(
    public readonly left: VibeCurrency,
    public readonly right: VibeCurrency
  ) {
    super(`Cannot combine ${left} and ${right} money`);
    this.name = "CurrencyMismatchError";
  }
}

/**
 * Construct money from an integer ten-thousandths amount. Throws on a non-
 * integer — callers that multiply/convert must round (see {@link scaleMoney})
 * before they reach here, so a fractional amount signals a precision bug.
 */
export function money(currency: VibeCurrency, amountTenThousandths: number): Money {
  if (!Number.isInteger(amountTenThousandths)) {
    throw new Error(`Money amount must be an integer ten-thousandths, got ${amountTenThousandths}`);
  }
  return { currency, amountTenThousandths };
}

/** The additive identity for a currency. */
export function zeroMoney(currency: VibeCurrency): Money {
  return { currency, amountTenThousandths: 0 };
}

/** Sum two amounts of the same currency. Throws {@link CurrencyMismatchError} otherwise. */
export function addMoney(a: Money, b: Money): Money {
  if (a.currency !== b.currency) {
    throw new CurrencyMismatchError(a.currency, b.currency);
  }
  return {
    currency: a.currency,
    amountTenThousandths: a.amountTenThousandths + b.amountTenThousandths
  };
}

/** Sum a list of amounts; an empty list yields `zeroMoney(currency)`. */
export function sumMoney(currency: VibeCurrency, amounts: readonly Money[]): Money {
  return amounts.reduce((acc, m) => addMoney(acc, m), zeroMoney(currency));
}

/**
 * Multiply by a (possibly fractional) scalar and round UP to the next whole
 * ten-thousandth. `Math.ceil` mirrors the token-billing convention
 * (`computePriceFromTokens`) — we never under-charge for a partial unit.
 */
export function scaleMoney(m: Money, factor: number): Money {
  return {
    currency: m.currency,
    amountTenThousandths: ceilTenThousandths(m.amountTenThousandths * factor)
  };
}

/** Format as `"<CURRENCY> <whole>.<4dp>"` for logs and operator surfaces (never i18n display). */
export function formatMoney(m: Money): string {
  return `${m.currency} ${(m.amountTenThousandths / VIBE_MONEY_PRECISION).toFixed(4)}`;
}
